/* eslint-disable */
// @ts-nocheck
/* ============================================================================
 * MTE — ManipulationGlisser UI (Track J.3b — 2026-09-14)
 * ----------------------------------------------------------------------------
 * Source-of-truth : shared/mte-product/03-skill-taxonomy.md
 * Owner : Atelier (Track J.3b)
 *
 * Archetype : "manipulation-glisser" (SVG canvas drag-drop — placer un chip
 *             sur la position correcte d'une droite graduée : fraction,
 *             décimal, nombre, etc.)
 *
 * Props (canonical, see lib/banque-types.ts):
 *   - item          : BanqueItemManipulationGlisser (spec: Record<string, unknown>)
 *   - onAnswer      : (correct: boolean, payload?: unknown) => void
 *   - disabled?     : boolean
 *   - showFeedback? : 'idle' | 'correct' | 'incorrect'
 *
 * Spec contract (free-form, this UI expects keys below with sane defaults) :
 *   - kind           : 'fraction-on-numberline' | 'decimal-on-numberline' | 'number-on-numberline'
 *   - min, max       : number (inclusive bounds of the numberline)
 *   - tickStep       : number (visual ticks every N units; min=1)
 *   - cible          : number (correct position on the line)
 *   - tolerance      : number (default 0.05 = 5% of (max-min))
 *   - unite          : string (default '' for plain number, 'fraction' shows numerator/denominator)
 *   - numerator      : number (when unite === 'fraction')
 *   - denominator    : number (when unite === 'fraction')
 *
 * Payload returned via onAnswer : { position: number, distance: number, correct: boolean }
 * ========================================================================== */

import React, { useState, useMemo, useRef } from 'react';
import type { BanqueItemManipulationGlisser } from '../../../../lib/banque-types';

export interface ManipulationGlisserProps {
  item: BanqueItemManipulationGlisser;
  onAnswer: (correct: boolean, payload?: unknown) => void;
  disabled?: boolean;
  showFeedback?: 'idle' | 'correct' | 'incorrect';
}

interface SpecResolved {
  kind: 'fraction-on-numberline' | 'decimal-on-numberline' | 'number-on-numberline';
  min: number;
  max: number;
  tickStep: number;
  cible: number;
  tolerance: number;
  unite: 'number' | 'decimal' | 'fraction';
  numerator?: number;
  denominator?: number;
}

function resolveSpec(spec: Record<string, unknown>): SpecResolved {
  const min = Number(spec.min ?? 0);
  const max = Number(spec.max ?? 10);
  const tickStep = Number(spec.tickStep ?? 1);
  const unite = (spec.unite as 'number' | 'decimal' | 'fraction') ?? 'number';
  const kindRaw = (spec.kind as string) ?? (
    unite === 'fraction'
      ? 'fraction-on-numberline'
      : unite === 'decimal'
      ? 'decimal-on-numberline'
      : 'number-on-numberline'
  );
  const kind = (
    kindRaw === 'fraction-on-numberline' ||
    kindRaw === 'decimal-on-numberline' ||
    kindRaw === 'number-on-numberline'
      ? kindRaw
      : 'number-on-numberline'
  ) as SpecResolved['kind'];
  const cible = Number(spec.cible ?? min);
  const tolerance = Number(spec.tolerance ?? 0.05);
  return {
    kind,
    min,
    max,
    tickStep,
    cible,
    tolerance,
    unite,
    numerator: spec.numerator !== undefined ? Number(spec.numerator) : undefined,
    denominator: spec.denominator !== undefined ? Number(spec.denominator) : undefined,
  };
}

const CANVAS_W = 480;
const CANVAS_H = 120;
const MARGIN_X = 32;
const MARGIN_Y = 60;
const LINE_Y = MARGIN_Y;
const CHIP_SIZE = 36;

function valueToX(value: number, min: number, max: number): number {
  const range = max - min;
  if (range === 0) return MARGIN_X;
  return MARGIN_X + ((value - min) / range) * (CANVAS_W - 2 * MARGIN_X);
}

function xToValue(x: number, min: number, max: number): number {
  const range = max - min;
  const usable = CANVAS_W - 2 * MARGIN_X;
  if (usable === 0) return min;
  return min + ((x - MARGIN_X) / usable) * range;
}

export function ManipulationGlisser({
  item,
  onAnswer,
  disabled = false,
  showFeedback = 'idle',
}: ManipulationGlisserProps): JSX.Element {
  const spec = useMemo(() => resolveSpec(item.spec ?? {}), [item.spec]);
  const [positionX, setPositionX] = useState<number>(valueToX(spec.min, spec.min, spec.max));
  const [submitted, setSubmitted] = useState<boolean>(false);
  const draggingRef = useRef<boolean>(false);

  const currentValue = useMemo(
    () => xToValue(positionX, spec.min, spec.max),
    [positionX, spec.min, spec.max],
  );
  const distance = useMemo(
    () => Math.abs(currentValue - spec.cible),
    [currentValue, spec.cible],
  );
  const isCorrectNow = useMemo(
    () => distance <= spec.tolerance,
    [distance, spec.tolerance],
  );

  const handlePointerDown = (e: React.PointerEvent<SVGGElement>): void => {
    if (disabled || submitted) return;
    draggingRef.current = true;
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };
  const handlePointerMove = (e: React.PointerEvent<SVGGElement>): void => {
    if (!draggingRef.current || disabled || submitted) return;
    const svg = (e.currentTarget as SVGGElement).ownerSVGElement;
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    const local = pt.matrixTransform(ctm.inverse());
    const clamped = Math.max(MARGIN_X, Math.min(CANVAS_W - MARGIN_X, local.x));
    setPositionX(clamped);
  };
  const handlePointerUp = (e: React.PointerEvent<SVGGElement>): void => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    (e.target as Element).releasePointerCapture?.(e.pointerId);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (disabled || submitted) return;
    setSubmitted(true);
    onAnswer(isCorrectNow, {
      position: currentValue,
      distance,
      cible: spec.cible,
    });
  };

  const ticks = useMemo(() => {
    const out: Array<{ value: number; x: number }> = [];
    const range = spec.max - spec.min;
    if (range === 0 || spec.tickStep <= 0) return out;
    for (let v = spec.min; v <= spec.max + 0.0001; v += spec.tickStep) {
      const x = valueToX(v, spec.min, spec.max);
      out.push({ value: Math.round(v * 1000) / 1000, x });
    }
    return out;
  }, [spec.min, spec.max, spec.tickStep]);

  const feedbackClass = submitted
    ? showFeedback !== 'idle'
      ? showFeedback
      : isCorrectNow
      ? 'correct'
      : 'incorrect'
    : 'idle';

  const labelChip =
    spec.unite === 'fraction' && spec.numerator !== undefined && spec.denominator !== undefined
      ? `${spec.numerator}/${spec.denominator}`
      : spec.unite === 'decimal'
      ? currentValue.toFixed(2).replace(/\.?0+$/, '')
      : String(Math.round(currentValue * 100) / 100);

  return (
    <form
      className="manipulation-glisser"
      data-testid="manipulation-glisser"
      data-archetype="manipulation-glisser"
      data-item-id={item.id}
      data-feedback={feedbackClass}
      onSubmit={handleSubmit}
    >
      <p className="manipulation-glisser-enonce" aria-label="Énoncé">
        {item.enonce}
      </p>
      {item.consigne && (
        <p className="manipulation-glisser-consigne">{item.consigne}</p>
      )}
      <svg
        className="manipulation-glisser-canvas"
        viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
        role="img"
        aria-label="Droite graduée interactive"
        data-testid="manipulation-glisser-canvas"
      >
        <line
          x1={MARGIN_X}
          x2={CANVAS_W - MARGIN_X}
          y1={LINE_Y}
          y2={LINE_Y}
          className="manipulation-glisser-line"
        />
        {ticks.map((t, i) => {
          // Stagger alternant top/bottom pour éviter chevauchement des labels (BUG #6 Ana-direct 19/09 ~16:30)
          const stagger = i % 2 === 0 ? 'below' : 'above';
          const labelY = stagger === 'below' ? LINE_Y + 22 : LINE_Y - 14;
          return (
            <g key={`tick-${i}`}>
              <line
                x1={t.x}
                x2={t.x}
                y1={LINE_Y - 6}
                y2={LINE_Y + 6}
                className="manipulation-glisser-tick"
              />
              <text
                x={t.x}
                y={labelY}
                textAnchor="middle"
                className={`manipulation-glisser-tick-label manipulation-glisser-tick-label-${stagger}`}
              >
                {t.value}
              </text>
            </g>
          );
        })}
        <g
          className="manipulation-glisser-chip"
          data-testid="manipulation-glisser-chip"
          data-position={currentValue.toFixed(3)}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onKeyDown={(e) => {
            if (disabled || submitted) return;
            // BUG #6 (Ana-direct 19/09 ~16:30): keyboard arrows ajustam o chip em 1 tickStep
            const step = spec.tickStep;
            const cur = xToValue(positionX, spec.min, spec.max);
            let next = cur;
            if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = cur - step;
            else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = cur + step;
            else return;
            e.preventDefault();
            const clampedV = Math.max(spec.min, Math.min(spec.max, next));
            setPositionX(valueToX(clampedV, spec.min, spec.max));
          }}
          role="slider"
          aria-label="Position du chip sur la droite"
          aria-valuenow={Math.round(currentValue * 100) / 100}
          aria-valuemin={spec.min}
          aria-valuemax={spec.max}
          tabIndex={disabled || submitted ? -1 : 0}
        >
          {/* BUG #6: hit-area invisível maior para acessibilidade táctil */}
          <circle cx={positionX} cy={LINE_Y} r={CHIP_SIZE / 2 + 6} className="manipulation-glisser-chip-hit" />
          <circle cx={positionX} cy={LINE_Y} r={CHIP_SIZE / 2} />
          <text x={positionX} y={LINE_Y + 4} textAnchor="middle">
            {labelChip}
          </text>
        </g>
      </svg>
      <button
        type="submit"
        className="manipulation-glisser-submit"
        data-action="soumettre"
        data-testid="manipulation-glisser-submit"
        disabled={disabled || submitted}
      >
        Valider
      </button>
      {submitted && (
        <p
          className="manipulation-glisser-feedback"
          data-testid="manipulation-glisser-feedback"
        >
          {isCorrectNow
            ? 'Bravo, tu as placé le chip à la bonne position !'
            : `Ce n'est pas encore ça. La bonne position est près de ${spec.cible}.`}
        </p>
      )}
    </form>
  );
}

export default ManipulationGlisser;